// src/hooks/useTokenRefresh.js
// Automatisk fornyelse av access token (via refresh-cookie) mens brukeren er aktiv i appen.
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
const INTERVAL_MS = 10 * 60 * 1000;
const VISIBILITY_THROTTLE_MS = 30 * 1000;

/**
 * Bakgrunn:
 * - Backend setter accessToken-cookie med kort levetid (typisk 15 min).
 * - Uten periodisk refresh vil token utløpe selv om brukeren fortsatt «jobber» i skjemaet.
 * - Denne hooken kaller AuthContext.refreshToken() (POST /api/auth/refresh) med jevne mellomrom
 *   når brukeren nylig har vært aktiv, og umiddelbart når fanen får fokus igjen
 *   (lunsj-pause / bytte fane).
 */
export const useTokenRefresh = () => {
	const { isLoggedIn, refreshToken } = useAuth();
	const lastActivityRef = useRef(Date.now());
	const lastRefreshAtRef = useRef(0);
	const refreshIntervalRef = useRef(null);

	useEffect(() => {
		if (!isLoggedIn) {
			if (refreshIntervalRef.current) {
				clearInterval(refreshIntervalRef.current);
			}
			return;
		}

		const updateActivity = () => {
			lastActivityRef.current = Date.now();
		};

		const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
		events.forEach((event) => {
			window.addEventListener(event, updateActivity);
		});

		const refreshNow = async () => {
			lastRefreshAtRef.current = Date.now();
			lastActivityRef.current = Date.now();
			await refreshToken();
		};

		const onTabActive = () => {
			if (document.visibilityState && document.visibilityState !== 'visible') {
				return;
			}
			const sinceRefresh = Date.now() - lastRefreshAtRef.current;
			if (sinceRefresh < VISIBILITY_THROTTLE_MS) {
				updateActivity();
				return;
			}
			void refreshNow();
		};

		document.addEventListener('visibilitychange', onTabActive);
		window.addEventListener('focus', onTabActive);

		refreshIntervalRef.current = setInterval(async () => {
			const timeSinceActivity = Date.now() - lastActivityRef.current;
			if (timeSinceActivity < FIFTEEN_MINUTES_MS) {
				await refreshNow();
			}
		}, INTERVAL_MS);

		return () => {
			events.forEach((event) => {
				window.removeEventListener(event, updateActivity);
			});
			document.removeEventListener('visibilitychange', onTabActive);
			window.removeEventListener('focus', onTabActive);
			if (refreshIntervalRef.current) {
				clearInterval(refreshIntervalRef.current);
			}
		};
	}, [isLoggedIn, refreshToken]);
};

export default useTokenRefresh;
